import React, { useState } from 'react'
import { Button, DatePicker, Icon, Table, Tag, message } from 'antd'
import moment from 'moment'
import {
  getBeaconDeviceId,
  getBeaconRssi,
  getBeaconTimestamp
} from './helpers'

const PRESETS = [
  { label: '1h', hours: 1 },
  { label: '3h', hours: 3 },
  { label: '6h', hours: 6 },
  { label: '12h', hours: 12 },
  { label: '24h', hours: 24 },
  { label: '48h', hours: 48 },
]

// d = 10 ^ ((MeasuredPower − RSSI) / 10n), MeasuredPower = −60 dBm (RSSI at 1m), n = 4
function calcDistance(rssiRaw) {
  const rssi = Number(rssiRaw)
  if (!rssiRaw || Number.isNaN(rssi) || rssi === 0) return null
  const d = Math.pow(10, (-60 - rssi) / 40)
  if (d < 1) return '<1m'
  if (d >= 1000) return `${(d / 1000).toFixed(2)}km`
  return `${Math.round(d)}m`
}

const STATUS_COLORS = {
  FOUND: 'green',
  LOST: 'red',
  ACTIVE: 'blue',
  ONLINE: 'cyan',
  OFFLINE: 'orange',
}

function buildColumns(detectorAssetsMap) {
  return [
    {
      title: 'Time',
      key: 'time',
      width: 150,
      defaultSortOrder: 'descend',
      sorter: (a, b) => (Number(getBeaconTimestamp(a)) || 0) - (Number(getBeaconTimestamp(b)) || 0),
      render: (_, record) => {
        const ts = getBeaconTimestamp(record)
        return ts ? moment(Number(ts)).format('DD-MM-YY HH:mm:ss') : '-'
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 70,
      render: (_, record) => {
        const s = (record.status || '').toUpperCase()
        return s ? <Tag color={STATUS_COLORS[s] || 'geekblue'} style={{ margin: 0 }}>{s}</Tag> : '-'
      },
    },
    {
      title: 'RSSI',
      key: 'rssi',
      width: 100,
      render: (_, record) => {
        const rssi = getBeaconRssi(record)
        const d = calcDistance(rssi)
        return d ? `${rssi} (~${d})` : (rssi || '-')
      },
    },
    {
      title: 'Reader Asset',
      key: 'reader',
      ellipsis: true,
      render: (_, record) => {
        const id = getBeaconDeviceId(record)
        const info = detectorAssetsMap && detectorAssetsMap[id]
        const assetLabel = info ? (info.display || info.assetCode || id) : '-'
        return (
          <span>
            {assetLabel}
            {id && id !== '-' && (
              <span style={{ display: 'block', fontSize: 11, opacity: 0.55, lineHeight: 1.3 }}>{id}</span>
            )}
          </span>
        )
      },
    },
  ]
}

function BeaconPeriodSelector({ loading, onShow, onClear, historyData = [], detectorAssetsMap = {}, selectedRecord = null, onRowClick, buttonLabel = 'Show History', columns: columnsProp }) {
  const [active, setActive] = useState('1h')
  const [customFrom, setCustomFrom] = useState(moment().subtract(1, 'hours'))
  const [customTo, setCustomTo] = useState(moment())

  const getPresetHours = () => {
    const preset = PRESETS.find(p => p.label === active)
    return preset ? preset.hours : null
  }

  const handleShowHistory = () => {
    if (active === 'custom') {
      if (!customFrom || !customTo) {
        message.error('Please select both From and To datetime.')
        return
      }
      const diffHours = customTo.diff(customFrom, 'hours', true)
      if (diffHours <= 0) {
        message.error('From must be earlier than To.')
        return
      }
      if (diffHours > 48) {
        message.error('Range cannot exceed 48 hours.')
        return
      }
      onShow([customFrom.valueOf(), customTo.valueOf()])
    } else {
      const hours = getPresetHours()
      const to = moment()
      const from = moment().subtract(hours, 'hours')
      onShow([from.valueOf(), to.valueOf()])
    }
  }

  const getCustomDuration = () => {
    if (!customFrom || !customTo) return null
    const diffMin = customTo.diff(customFrom, 'minutes')
    if (diffMin <= 0) return null
    const h = Math.floor(diffMin / 60)
    const m = diffMin % 60
    return h > 0 ? `${h}h${m > 0 ? ' ' + m + 'm' : ''}` : `${m}m`
  }

  const duration = active === 'custom' ? getCustomDuration() : null
  const hasHistory = historyData && historyData.length > 0
  const columns = columnsProp || buildColumns(detectorAssetsMap)
  const selectedTs = selectedRecord ? String(getBeaconTimestamp(selectedRecord)) : null

  return (
    <div className="beacon-period-selector">
      <p className="beacon-period-label-text">Select Time Period</p>

      <div className="beacon-preset-chips">
        {PRESETS.map(preset => (
          <button
            key={preset.label}
            className={`beacon-preset-chip${active === preset.label ? ' active' : ''}`}
            onClick={() => setActive(preset.label)}
            disabled={loading}
          >
            {preset.label}
          </button>
        ))}
        <button
          className={`beacon-preset-chip${active === 'custom' ? ' active' : ''}`}
          onClick={() => setActive('custom')}
          disabled={loading}
        >
          Custom
        </button>
      </div>

      {active === 'custom' && (
        <div className="beacon-period-custom">
          <div className="beacon-period-field">
            <span className="beacon-period-field-label">From</span>
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="DD-MM-YYYY HH:mm"
              value={customFrom}
              onChange={(val) => setCustomFrom(val)}
              disabledDate={(d) => d && d.isAfter(moment(), 'day')}
              placeholder="Start date & time"
              style={{ width: '100%' }}
            />
          </div>
          <div className="beacon-period-field">
            <span className="beacon-period-field-label">To</span>
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="DD-MM-YYYY HH:mm"
              value={customTo}
              onChange={(val) => setCustomTo(val)}
              disabledDate={(d) => d && (d.isAfter(moment(), 'day') || (customFrom && d.isBefore(customFrom, 'day')))}
              placeholder="End date & time"
              style={{ width: '100%' }}
            />
          </div>
          {duration && (
            <div className="beacon-period-duration">
              <Icon type="clock-circle" /> {duration} selected
            </div>
          )}
        </div>
      )}

      <Button
        type="primary"
        icon="search"
        loading={loading}
        onClick={handleShowHistory}
        style={{ width: '100%', marginBottom: hasHistory ? 16 : 0 }}
      >
        {buttonLabel}
      </Button>

      {hasHistory && (
        <div className="beacon-history-table-wrap">
          <div className="beacon-history-table-header">
            <span><Icon type="environment" /> {historyData.length} record(s)</span>
            <Button icon="close-circle" size="small" onClick={onClear}>Clear</Button>
          </div>
          <Table
            dataSource={historyData}
            columns={columns}
            rowKey={(r, i) => i}
            size="small"
            pagination={{ pageSize: 10, size: 'small', showTotal: (total) => `${total} records` }}
            scroll={{ x: true }}
            rowClassName={(record) => {
              const ts = String(getBeaconTimestamp(record))
              return ts && ts === selectedTs ? 'beacon-history-row--selected' : ''
            }}
            onRow={(record) => ({
              onClick: () => onRowClick && onRowClick(record),
              style: { cursor: 'pointer' },
            })}
          />
        </div>
      )}
    </div>
  )
}

export default BeaconPeriodSelector
